/**
 * The "Nästa steg" card after a terminal answer (pedagogy spec §5.3.1): the contrast twins on offer
 * (C-6, `ContrastOffers`: a prediction first, then the twin) and the day Today will re-test the item
 * (row iii, from `QuestionResult.retestDue`). The server sends the day on every answer that will
 * come back (a wrong or assisted one) and none on an unaided first-try success; a row with
 * nothing to say is left out, and with no row at all neither is the card. The date reads as
 * "tors 25 sep" (`formatDayShort`).
 */
import { CalendarDays } from "lucide-react";
import { useId } from "react";
import { Badge } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { ContrastOffers } from "./ContrastOffers";
import type { ContrastOfferView } from "./followUp";
import type { ContrastOffer } from "./types";

export interface NextStepsProps {
  /** `YYYY-MM-DD`, the re-test day; absent when nothing comes back. */
  retestDue?: string;
  /** The contrast twins the answer offered, as the page sees them (empty when there are none). */
  contrasts?: readonly ContrastOfferView[];
  /** Insert a twin with the prediction made for it (`undefined` when its link authors none). */
  onStartContrast?: (offer: ContrastOffer, choice: string | undefined) => void;
}

export function NextSteps({ retestDue, contrasts = [], onStartContrast }: NextStepsProps) {
  const t = useT();
  const { lang } = useLang();
  const labelId = useId();
  if (!retestDue && contrasts.length === 0) return null;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="basis-full rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-left dark:border-ink-800 dark:bg-ink-900"
    >
      <p
        id={labelId}
        className="text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400"
      >
        {t("practice.next.title")}
      </p>
      {contrasts.length > 0 && (
        <div className="mt-2">
          <ContrastOffers
            views={contrasts}
            onStart={(offer, choice) => onStartContrast?.(offer, choice)}
          />
        </div>
      )}
      {retestDue && (
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-700 dark:text-ink-200">
          <CalendarDays
            className="h-4 w-4 shrink-0 text-ink-400 dark:text-ink-500"
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1">
            {t("practice.next.comesBack", { date: formatDayShort(retestDue, lang) })}
          </span>
          <Badge tone="neutral">{t("practice.next.comesBackBadge")}</Badge>
        </p>
      )}
    </div>
  );
}
